import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { Bot, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { api } from "../../../../api/client";
import { useActiveCanvasId } from "../../../../contexts/ActiveCanvasIdContext";
import { useProjectId } from "../../../../contexts/ProjectIdContext";
import { useBooleanFlagValue } from "../../../../hooks/useBooleanFlagValue";
import { createNadDebug } from "../../../../utils/debugLog";
import type { ToolbarAction } from "./AgentNodeToolbar";
import {
  aiToolsOperationKey,
  getAiToolsOperation,
  startAiToolsOperation,
  subscribeAiToolsOperation,
} from "./aiToolsOperationStore";

const depthVideoLog = createNadDebug("dw:depth-video:client");

/** Return whether depth actions are enabled by the runtime feature flag. */
export function depthVideoUiAvailable(flagEnabled: boolean): boolean {
  return flagEnabled;
}

/** Expose enabled depth-audio choices through the shared operation lock. */
export function useDepthVideoAction({
  nodeId,
  hasVideo,
}: {
  nodeId: string;
  hasVideo: boolean;
}): ToolbarAction {
  const { t } = useTranslation("canvas");
  const projectId = useProjectId();
  const canvasId = useActiveCanvasId();
  const flagEnabled = useBooleanFlagValue("depth_video_enabled", false);
  const depthVisible = depthVideoUiAvailable(flagEnabled);
  // In-flight state lives in a module store, not useState: ReactFlow remounts
  // nodes that scroll out of / back into the viewport, and a component-local
  // flag reset mid-request (re-enabling the button → duplicate clip groups).
  const operationKey = projectId ? aiToolsOperationKey(projectId, canvasId, nodeId) : null;
  const subscribe = useCallback(
    (listener: () => void) =>
      operationKey ? subscribeAiToolsOperation(operationKey, listener) : () => undefined,
    [operationKey],
  );
  const getSnapshot = useCallback(
    () => (operationKey ? getAiToolsOperation(operationKey) : null),
    [operationKey],
  );
  const activeOperation = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    depthVideoLog({
      event: "ai-tools:mount",
      project_id: projectId ?? null,
      canvas_id: canvasId,
      source_node_id: nodeId,
      active_operation: operationKey ? getAiToolsOperation(operationKey) : null,
    });
    return () =>
      depthVideoLog({
        event: "ai-tools:unmount",
        project_id: projectId ?? null,
        canvas_id: canvasId,
        source_node_id: nodeId,
        active_operation: operationKey ? getAiToolsOperation(operationKey) : null,
      });
  }, [canvasId, nodeId, operationKey, projectId]);

  const onCreateDepth = useCallback((includeAudio: boolean) => {
    if (!projectId || !operationKey || !depthVisible || !hasVideo) return;
    const pending = startAiToolsOperation(operationKey, "depth", () =>
      api.createDepthVideo(projectId, nodeId, canvasId, includeAudio),
    );
    if (!pending) return;
    depthVideoLog({
      event: "depth-video:create_requested",
      include_audio: includeAudio,
      feature: "depth-video",
      concern: "client",
      project_id: projectId,
      canvas_id: canvasId,
      source_node_id: nodeId,
    });
    void pending
      .then(({ nodeId: createdNodeId }) => {
        depthVideoLog({
          event: "depth-video:create_scheduled",
          include_audio: includeAudio,
          feature: "depth-video",
          concern: "client",
          project_id: projectId,
          canvas_id: canvasId,
          source_node_id: nodeId,
          depth_node_id: createdNodeId,
        });
        toast.success(t("depthVideo.createStarted"));
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error);
        depthVideoLog({
          event: "depth-video:create_failed",
          include_audio: includeAudio,
          feature: "depth-video",
          concern: "client",
          project_id: projectId,
          canvas_id: canvasId,
          source_node_id: nodeId,
          reason: message,
        });
        toast.error(t("depthVideo.createFailed", { message }));
      });
  }, [canvasId, depthVisible, hasVideo, nodeId, operationKey, projectId, t]);

  const onCreateAiClips = useCallback(() => {
    if (!projectId || !operationKey || !hasVideo) return;
    const pending = startAiToolsOperation(operationKey, "ai-clip", () =>
      api.createAiClips(projectId, nodeId, canvasId),
    );
    if (!pending) return;
    depthVideoLog({
      event: "ai-clip:create_requested",
      feature: "ai-clip",
      concern: "client",
      project_id: projectId,
      canvas_id: canvasId,
      source_node_id: nodeId,
    });
    void pending
      .then(({ groupId, sceneCount }) => {
        depthVideoLog({
          event: "ai-clip:group_created",
          feature: "ai-clip",
          concern: "client",
          project_id: projectId,
          canvas_id: canvasId,
          source_node_id: nodeId,
          group_id: groupId,
          scene_count: sceneCount,
        });
        toast.success(t("depthVideo.aiClipCreated", { count: sceneCount }));
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error);
        depthVideoLog({
          event: "ai-clip:create_failed",
          feature: "ai-clip",
          concern: "client",
          project_id: projectId,
          canvas_id: canvasId,
          source_node_id: nodeId,
          reason: message,
        });
        toast.error(t("depthVideo.aiClipFailed", { message }));
      });
  }, [canvasId, hasVideo, nodeId, operationKey, projectId, t]);

  return useMemo(
    () => ({
      key: "ai-tools",
      label: t("depthVideo.aiTools"),
      icon: activeOperation ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Bot aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
      ),
      disabled: !hasVideo || activeOperation !== null,
      menuItems: [
        {
          key: "ai-clip",
          label: t("depthVideo.aiClipAction"),
          onClick: onCreateAiClips,
          disabled: !hasVideo || activeOperation !== null,
        },
        {
          key: "extract-depth-with-audio",
          label: t("depthVideo.createWithAudioAction"),
          onClick: () => onCreateDepth(true),
          disabled: !hasVideo || activeOperation !== null,
          hidden: !depthVisible,
        },
        {
          key: "extract-depth",
          label: t("depthVideo.createWithoutAudioAction"),
          onClick: () => onCreateDepth(false),
          disabled: !hasVideo || activeOperation !== null,
          hidden: !depthVisible,
        },
      ],
    }),
    [activeOperation, depthVisible, hasVideo, onCreateAiClips, onCreateDepth, t],
  );
}
